import React from "react";
import { useIntl } from "react-intl";
import { Button, Label } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { useInstagramCore } from "../../../../cores/instagram";
import { InstagramAccount as Account } from "../../../../cores/instagram/interface/api/get-instagram-accounts";
import { InstagramIcon } from "./InstagramIcon";
import { FacebookIcon } from "../Facebook/FacebookIcon";
import { INSTAGRAM_COLOR } from "../../../../constants/socialColors.constant";
import { FACEBOOK_COLOR } from "../../../../constants/socialColors.constant";
import "./InstagramAccounts.scss";

interface Props {
  onSelectAccount: (account: Account) => void;
  selectedAccount: Account | null;
  onBindAccount?: (account: Account) => void;
  binding?: boolean;
}

export default function InstagramAccounts({
  onSelectAccount,
  selectedAccount,
  onBindAccount,
  binding = false,
}: Props) {
  const intl = useIntl();
  const { accounts, facebookProfileName } = useInstagramCore();

  return (
    <div className="instagram-linked-accounts px-2">
      <div className="instagram-linked-accounts__header">
        <p className="instagram-linked-accounts__title">
          {intl.formatMessage({ id: "app.syncInstagram.linkedAccountsTitle" })}
        </p>
        {facebookProfileName && (
          <p className="instagram-linked-accounts__subtitle">
            {intl.formatMessage(
              { id: "app.syncInstagram.linkedAccountsSubtitle" },
              { profileName: facebookProfileName }
            )}
          </p>
        )}
      </div>

      <div className="instagram-linked-accounts__list">
        {accounts.map((account) => (
          <LinkedAccountRow
            key={account.id}
            account={account}
            selected={account.id === selectedAccount?.id}
            binding={binding && account.id === selectedAccount?.id}
            onSelect={() => {
              onSelectAccount(account);
              onBindAccount?.(account);
            }}
          />
        ))}
      </div>

      {accounts.length === 0 && (
        <Label className="instagram-linked-accounts__hint d-block">
          {intl.formatMessage({ id: "app.syncInstagram.txt1" })}{" "}
          <a
            href="https://soporte.wembii.com/es/article/como-cambiar-una-cuenta-de-instagram-a-empresa-1d9gri7/?bust=1618583393673"
            target="_blank"
            rel="noopener noreferrer"
          >
            {intl.formatMessage({ id: "app.syncInstagram.txt2" })}
          </a>{" "}
          {intl.formatMessage({ id: "app.syncInstagram.txt3" })}
        </Label>
      )}
    </div>
  );
}

const LinkedAccountRow = ({
  account,
  selected,
  binding,
  onSelect,
}: {
  account: Account;
  selected: boolean;
  binding: boolean;
  onSelect: () => void;
}) => {
  const intl = useIntl();
  const igHandle = account.userName
    ? `@${account.userName}`
    : account.name;

  return (
    <div
      className={`instagram-linked-accounts__row${
        selected ? " instagram-linked-accounts__row--selected" : ""
      }`}
    >
      {account.profilePictureUrl && (
        <img
          className="instagram-linked-accounts__avatar"
          src={account.profilePictureUrl}
          alt=""
        />
      )}
      <div className="instagram-linked-accounts__pair">
        <span className="instagram-linked-accounts__ig">
          <InstagramIcon size="sm" color={INSTAGRAM_COLOR} />
          {igHandle}
        </span>
        <FontAwesomeIcon
          icon={faArrowRight}
          className="instagram-linked-accounts__arrow"
        />
        <span className="instagram-linked-accounts__page">
          <FacebookIcon size="sm" color={FACEBOOK_COLOR} />
          {account.pageName || account.name}
        </span>
      </div>
      <Button
        color="primary"
        size="sm"
        className="flex-shrink-0"
        disabled={binding}
        onClick={onSelect}
      >
        {binding
          ? intl.formatMessage({ id: "app.syncInstagram.connecting" })
          : intl.formatMessage({ id: "app.syncInstagram.select" })}
      </Button>
    </div>
  );
};
